import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  Skeleton
} from '@mui/material'
import {
  Visibility,
  Edit,
  PictureAsPdf,
  FilterList,
  Business,
  Person,
  DateRange
} from '@mui/icons-material'
import { useQuery } from 'react-query'
import axios from 'axios'

import SearchBar from './SearchBar'
import LoadingSpinner from './LoadingSpinner'
import { useAuth } from '../../contexts/AuthContext'
import { useGestiones } from '../../hooks/useProcedures'

const SearchPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { hasPermission, canAccessGestion } = useAuth()
  
  const [searchParams, setSearchParams] = useState({
    search: '',
    gestion_id: '',
    estado: '',
    page: 1,
    limit: 12
  })
  
  const [showFilters, setShowFilters] = useState(false)
  
  // Extraer parámetros de la URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const newParams = {
      search: urlParams.get('q') || '',
      gestion_id: urlParams.get('gestion') || '',
      estado: urlParams.get('estado') || '',
      page: parseInt(urlParams.get('page')) || 1,
      limit: 12
    }
    setSearchParams(newParams)
  }, [location.search])

  // Cargar gestiones
  const { gestiones } = useGestiones()

  // Query para búsqueda de procedimientos
  const { data: searchResults, isLoading, error } = useQuery(
    ['search-procedimientos', searchParams],
    async () => {
      const params = new URLSearchParams()
      
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value)
        }
      })

      const token = localStorage.getItem('sgc_token')
      const response = await axios.get(`/api/procedimientos?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      return response.data
    },
    {
      keepPreviousData: true,
      staleTime: 30 * 1000,
    }
  )

  const updateURL = (newParams) => {
    const params = new URLSearchParams()

    Object.entries(newParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && key !== 'limit') {
        // Mapear nombres de parámetros internos a nombres de URL
        let urlKey = key
        if (key === 'search') urlKey = 'q'
        if (key === 'gestion_id') urlKey = 'gestion'

        params.append(urlKey, value)
      }
    })

    const newSearch = params.toString()
    navigate(`/buscar${newSearch ? '?' + newSearch : ''}`, { replace: true })
  }

  const handleSearch = (searchTerm) => {
    const newParams = { ...searchParams, search: searchTerm, page: 1 }
    setSearchParams(newParams)
    updateURL(newParams)
  }

  const handleFilterChange = (filterName, value) => {
    const newParams = { ...searchParams, [filterName]: value, page: 1 }
    setSearchParams(newParams)
    updateURL(newParams)
  }

  const handlePageChange = (event, page) => {
    const newParams = { ...searchParams, page }
    setSearchParams(newParams)
    updateURL(newParams)
  }

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'aprobado':
        return 'success'
      case 'revision':
        return 'warning'
      case 'borrador':
        return 'default'
      case 'archivado':
        return 'error'
      default:
        return 'default'
    }
  }

  const handleViewProcedure = (id) => {
    navigate(`/procedimientos/${id}`)
  }

  const handleEditProcedure = (id) => {
    navigate(`/procedimientos/${id}/editar`)
  }

  const handleExportPDF = (id) => {
    window.open(`/api/export/procedimientos/${id}/pdf`, '_blank')
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error cargando resultados de búsqueda: {error.message}
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header de búsqueda */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Búsqueda de Procedimientos
        </Typography>
        
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <SearchBar
              onSearch={handleSearch}
              onFilterChange={handleFilterChange}
              placeholder="Buscar por código, título, proceso o contenido..."
              filters={searchParams}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Button
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              variant={showFilters ? 'contained' : 'outlined'}
              fullWidth
            >
              Filtros Avanzados
            </Button>
          </Grid>
        </Grid>

        {/* Filtros avanzados */}
        {showFilters && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Gestión</InputLabel>
                <Select
                  value={searchParams.gestion_id}
                  onChange={(e) => handleFilterChange('gestion_id', e.target.value)}
                  label="Gestión"
                >
                  <MenuItem value="">Todas las gestiones</MenuItem>
                  {gestiones.map((gestion) => (
                    <MenuItem key={gestion.id} value={gestion.id}>
                      {gestion.codigo} - {gestion.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={searchParams.estado}
                  onChange={(e) => handleFilterChange('estado', e.target.value)}
                  label="Estado"
                >
                  <MenuItem value="">Todos los estados</MenuItem>
                  <MenuItem value="borrador">Borrador</MenuItem>
                  <MenuItem value="revision">En Revisión</MenuItem>
                  <MenuItem value="aprobado">Aprobado</MenuItem>
                  <MenuItem value="archivado">Archivado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Resultados */}
      {isLoading ? (
        <Box>
          <Grid container spacing={3}>
            {Array.from(new Array(6)).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card>
                  <CardContent>
                    <Skeleton variant="text" width="40%" height={24} />
                    <Skeleton variant="text" width="100%" height={20} />
                    <Skeleton variant="text" width="80%" height={16} />
                    <Box sx={{ mt: 1 }}>
                      <Skeleton variant="rectangular" width={60} height={24} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <>
          {/* Información de resultados */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchResults?.pagination?.total_records || 0} procedimientos encontrados
              {searchParams.search && (
                <> para "<strong>{searchParams.search}</strong>"</>
              )}
            </Typography>
          </Box>

          {/* Grid de resultados */}
          <Grid container spacing={3}>
            {searchResults?.procedimientos?.map((procedimiento) => (
              <Grid item xs={12} sm={6} md={4} key={procedimiento.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="h3" gutterBottom noWrap>
                        {procedimiento.codigo}
                      </Typography>
                      <Chip 
                        label={procedimiento.estado}
                        size="small"
                        color={getEstadoColor(procedimiento.estado)}
                      />
                    </Box>
                    
                    <Typography 
                      variant="body1" 
                      gutterBottom
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontWeight: 500
                      }}
                    >
                      {procedimiento.titulo}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      <Business sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      {procedimiento.gestion_nombre}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      <Person sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      {procedimiento.responsable}
                    </Typography>
                    
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ display: 'block' }}
                    >
                      <DateRange sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      Actualizado: {new Date(procedimiento.updated_at).toLocaleDateString('es-CO')}
                    </Typography>
                  </CardContent>
                  
                  <CardActions sx={{ pt: 0 }}>
                    <Button 
                      size="small" 
                      startIcon={<Visibility />}
                      onClick={() => handleViewProcedure(procedimiento.id)}
                    >
                      Ver
                    </Button>
                    
                    {hasPermission(['administrador', 'editor']) && 
                     canAccessGestion(procedimiento.gestion_id) && (
                      <Button 
                        size="small" 
                        startIcon={<Edit />}
                        onClick={() => handleEditProcedure(procedimiento.id)}
                      >
                        Editar
                      </Button>
                    )}
                    
                    <Tooltip title="Exportar a PDF">
                      <IconButton 
                        size="small"
                        onClick={() => handleExportPDF(procedimiento.id)}
                        color="error"
                      >
                        <PictureAsPdf />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            )) || []}
          </Grid>

          {/* Paginación */}
          {searchResults?.pagination && searchResults.pagination.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={searchResults.pagination.total_pages}
                page={searchParams.page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Estado vacío */}
          {searchResults?.procedimientos?.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No se encontraron procedimientos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Intenta ajustar los filtros de búsqueda o usar términos diferentes
              </Typography>
            </Paper>
          )}
        </>
      )}
    </Box>
  )
}

export default SearchPage